/**
 * Pick an ElevenLabs voice to fill the voice editor. Two sources, searched on the server:
 * "My voices" (the account's own, saved library voices and the default ones) and the public
 * "Voice library". Filters, Load more paging, preview playback, and "Add to account" for library
 * voices (narration can only use voices in the account).
 *
 * A 503 means the Admin API has no usable key; the message says where it goes.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FilterList, Search } from '@mui/icons-material'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { narrationApi } from '@api/endpoints/narration'
import type { ElevenLabsVoiceInfo, ElevenLabsVoiceSource } from '@api/types/narration'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  ELEVENLABS_FILTERS,
  elevenLabsListError,
  elevenLabsVoicesKey,
  filterLabel,
  narrationErrorMessage,
  supportsV3,
  voiceTraits,
} from './narrationForm'
import { PreviewButton } from './PreviewButton'
import { usePreviewAudio } from './usePreviewAudio'

const PAGE_SIZE = 30

interface Filters {
  language: string
  gender: string
  age: string
  accent: string
  category: string
  useCase: string
}

/** Descriptions longer than this get a "More" toggle; shorter ones fit the two clamped lines. */
const LONG_DESCRIPTION = 110

const NO_FILTERS: Filters = { language: '', gender: '', age: '', accent: '', category: '', useCase: '' }

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

export function ElevenLabsVoicePicker({
  open,
  onClose,
  onPick,
  existingIds,
}: {
  open: boolean
  onClose: () => void
  onPick: (voice: ElevenLabsVoiceInfo) => void
  /** ElevenLabs ids already in our list, badged so they are not added twice. */
  existingIds: ReadonlySet<string>
}) {
  const dialogProps = useMobileDialogProps()
  const queryClient = useQueryClient()
  const audio = usePreviewAudio()
  const [source, setSource] = useState<ElevenLabsVoiceSource>('account')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  /** Library voices added during this visit (ElevenLabs' own lists can lag). */
  const [added, setAdded] = useState<Record<string, ElevenLabsVoiceInfo>>({})
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  // Six filter fields wrap to four rows on a phone and push the voices off screen, so on xs
  // they sit behind a toggle.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const toggleExpanded = (id: string) =>
    setExpanded((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const debouncedSearch = useDebounced(search.trim(), 350)
  const debouncedAccent = useDebounced(filters.accent.trim(), 350)

  const params = { ...filters, accent: debouncedAccent, search: debouncedSearch }
  const query = useInfiniteQuery({
    queryKey: [...elevenLabsVoicesKey, source, params],
    queryFn: ({ pageParam }) =>
      narrationApi.elevenLabsVoices.search({ source, ...params, page: pageParam ?? undefined, pageSize: PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextPage : null),
    enabled: open,
    // The voices change rarely; a 503 is a config problem a retry will not fix.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const voices = useMemo(() => {
    const seen = new Set<string>()
    const list: ElevenLabsVoiceInfo[] = []
    for (const page of query.data?.pages ?? [])
      for (const v of page.items) {
        if (seen.has(v.voiceId)) continue
        seen.add(v.voiceId)
        list.push(added[v.voiceId] ?? v)
      }
    return list
  }, [query.data, added])
  const error = query.error ? elevenLabsListError(query.error) : null

  const add = useMutation({
    mutationFn: (v: ElevenLabsVoiceInfo) =>
      narrationApi.elevenLabsVoices.add(v.voiceId, { publicOwnerId: v.publicOwnerId, name: v.name }),
    onSuccess: (voice, original) => {
      setAdded((a) => ({ ...a, [original.voiceId]: { ...original, ...voice, isInAccount: true } }))
      // "My voices" now holds it.
      queryClient.invalidateQueries({ queryKey: [...elevenLabsVoicesKey, 'account'] })
    },
  })

  const close = () => {
    audio.stop()
    onClose()
  }

  const setFilter = (key: keyof Filters, value: string) => setFilters((f) => ({ ...f, [key]: value }))
  const categories = source === 'account' ? ELEVENLABS_FILTERS.accountCategory : ELEVENLABS_FILTERS.libraryCategory
  const anyFilter = Object.values(filters).some(Boolean)
  const filterCount = Object.values(filters).filter(Boolean).length

  const select = (key: keyof Filters, label: string, options: readonly string[], render = filterLabel) => (
    <TextField
      select
      size="small"
      label={label}
      value={filters[key]}
      onChange={(e) => setFilter(key, e.target.value)}
      sx={{ minWidth: 120, flex: { xs: '1 1 40%', sm: '1 1 120px' } }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {render(o)}
        </MenuItem>
      ))}
    </TextField>
  )

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth {...dialogProps}>
      <DialogTitle sx={{ pb: 0 }}>Pick an ElevenLabs voice</DialogTitle>
      <Tabs
        value={source}
        onChange={(_, v: ElevenLabsVoiceSource) => {
          setSource(v)
          // Category values differ between the sources.
          setFilter('category', '')
        }}
        sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="account" label="My voices" />
        <Tab value="library" label="Voice library" />
      </Tabs>
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={source === 'account' ? 'Search my voices…' : 'Search the ElevenLabs voice library…'}
            size="small"
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query.isFetching && !query.isFetchingNextPage ? <CircularProgress size={16} /> : undefined,
            }}
          />

          {isXs && (
            <Button
              size="small"
              variant={filterCount ? 'contained' : 'outlined'}
              startIcon={<FilterList />}
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              sx={{ alignSelf: 'flex-start' }}
            >
              Filters{filterCount ? ` (${filterCount})` : ''}
            </Button>
          )}

          <Collapse in={!isXs || filtersOpen}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              {select(
                'language',
                'Language',
                ELEVENLABS_FILTERS.language.map((l) => l.code),
                (code) => ELEVENLABS_FILTERS.language.find((l) => l.code === code)?.label ?? code,
              )}
              {select('gender', 'Gender', ELEVENLABS_FILTERS.gender)}
              {select('age', 'Age', ELEVENLABS_FILTERS.age)}
              <TextField
                size="small"
                label="Accent"
                placeholder="british"
                value={filters.accent}
                onChange={(e) => setFilter('accent', e.target.value)}
                sx={{ minWidth: 120, flex: { xs: '1 1 40%', sm: '1 1 120px' } }}
              />
              {select('category', 'Category', categories)}
              {select('useCase', 'Use case', ELEVENLABS_FILTERS.useCase)}
              {anyFilter && (
                <Button size="small" onClick={() => setFilters(NO_FILTERS)}>
                  Clear
                </Button>
              )}
            </Stack>
          </Collapse>

          {source === 'library' && (
            <Typography variant="caption" color="text.secondary">
              Narration can only use voices in the account. Use “Add to account” on a library voice before saving it.
            </Typography>
          )}

          {query.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity={error.notConfigured ? 'warning' : 'error'}>
              {error.title}
              {error.detail && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  Server said: {error.detail}
                </Typography>
              )}
            </Alert>
          )}

          {add.error && <Alert severity="error">{narrationErrorMessage(add.error, 'The voice could not be added.')}</Alert>}

          {query.data && voices.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {debouncedSearch || anyFilter ? 'No voices match.' : 'No voices.'}
            </Typography>
          )}

          {voices.length > 0 && (
            <List dense disablePadding sx={{ mx: -1 }}>
              {voices.map((v) => {
                const traits = voiceTraits(v)
                const v3 = supportsV3(v)
                const adding = add.isPending && add.variables?.voiceId === v.voiceId
                return (
                  // Actions sit under the text on a phone rather than as a secondaryAction, which
                  // reserved a fixed right gutter and squeezed the name and description.
                  <ListItem
                    key={v.voiceId}
                    disablePadding
                    divider
                    sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}
                  >
                    <ListItemButton
                      onClick={() => {
                        audio.stop()
                        onPick(v)
                      }}
                      sx={{ flex: 1, minWidth: 0, width: '100%' }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography component="span" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                              {v.name}
                            </Typography>
                            {v.category && <Chip size="small" variant="outlined" label={filterLabel(v.category)} />}
                            {v.isInAccount && <Chip size="small" color="success" variant="outlined" label="In account" />}
                            {v3 === true && <Chip size="small" variant="outlined" label="v3" />}
                            {existingIds.has(v.voiceId) && <Chip size="small" color="info" label="Already added" />}
                          </Stack>
                        }
                        secondary={
                          <>
                            {traits && <span>{traits}</span>}
                            {v.description && (
                              <Typography
                                variant="caption"
                                component="span"
                                sx={{
                                  overflowWrap: 'anywhere',
                                  ...(expanded.has(v.voiceId)
                                    ? { display: 'block' }
                                    : {
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }),
                                }}
                              >
                                {v.description}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}
                            >
                              {v.voiceId}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{ px: 1, pb: { xs: 1, sm: 0 }, flexShrink: 0 }}
                    >
                      {v.description && v.description.length > LONG_DESCRIPTION && (
                        <Button size="small" onClick={() => toggleExpanded(v.voiceId)}>
                          {expanded.has(v.voiceId) ? 'Less' : 'More'}
                        </Button>
                      )}
                      {!v.isInAccount && (
                        <Button size="small" variant="outlined" disabled={add.isPending} onClick={() => add.mutate(v)}>
                          {adding ? 'Adding…' : 'Add to account'}
                        </Button>
                      )}
                      <PreviewButton
                        url={v.previewUrl}
                        playing={audio.playing}
                        failed={audio.failed}
                        onToggle={audio.toggle}
                        label={v.name}
                      />
                    </Stack>
                  </ListItem>
                )
              })}
            </List>
          )}

          {query.hasNextPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {error && (
          <Button onClick={() => query.refetch()} disabled={query.isFetching}>
            Try again
          </Button>
        )}
        <Button onClick={close}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
